import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchLg } from '../../../../assets';
import { Section } from '../../../../components/ui/Section';
import Pagination from '../../../../components/forms/Pagination';
import ProductsList from './ProductsList';
import { MarketItem, MarketItemFilterOptions, client } from '../../../../app/services/api/orderManagementClient';
import useLogError from '../../../../hooks/useLogError';
import { useInstanceMarket } from '../../../../contexts/InstanceMarketContext';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type FilterChangeCallback = (
  instance: string,
  market: string,
  searchTerm?: string,
  sortColumn?: string,
  sortDirection?: 'asc' | 'desc',
  page?: number,
) => void;

type Props = {
  onCreateNew: () => void;
  onFilterChange?: FilterChangeCallback;
  initialInstance?: string;
  initialMarket?: string;
  initialSearchTerm?: string;
  initialSortColumn?: string;
  initialSortDirection?: 'asc' | 'desc';
  initialPage?: number;
}

const ProductsTab: FC<Props> = ({
  onCreateNew,
  onFilterChange,
  initialInstance,
  initialMarket,
  initialSearchTerm,
  initialSortColumn,
  initialSortDirection,
  initialPage,
}) => {
  const { t } = useTranslation('productManagement');
  const { logError } = useLogError();

  const [products, setProducts] = useState<MarketItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const { instances, getMarkets, ensureMarketsLoaded } = useInstanceMarket();
  const markets = getMarkets(selectedInstance);
  const [sortColumn, setSortColumn] = useState<string>('itemNumber');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Snackbar states
  const [showSubmitStatusMessage, setShowSubmitStatusMessage] = useState<boolean>(false);
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const pageSize = 20;

  useEffect(() => {
    if (initialInstance) setSelectedInstance(initialInstance);
    if (initialMarket) setSelectedMarket(initialMarket);
    if (initialSearchTerm !== undefined) {
      setSearchInput(initialSearchTerm);
      setSearchTerm(initialSearchTerm);
    }
    if (initialSortColumn) setSortColumn(initialSortColumn);
    if (initialSortDirection) setSortDirection(initialSortDirection);
    if (initialPage !== undefined) setCurrentPage(initialPage);
  }, [initialInstance, initialMarket, initialSearchTerm, initialSortColumn, initialSortDirection, initialPage]);

  useEffect(() => {
    if (!selectedInstance) return;
    ensureMarketsLoaded(selectedInstance);
  }, [selectedInstance, ensureMarketsLoaded]);

  useEffect(() => {
    if (selectedInstance && selectedMarket) {
      loadProducts();
    } else {
      setProducts([]);
      setTotalPages(0);
      setTotalItems(0);
    }
  }, [currentPage, searchTerm, selectedInstance, selectedMarket, sortColumn, sortDirection]);

  useEffect(() => {
    if (markets.length === 1) {
      const onlyMarket = markets[0].customerTypeId.toString();
      setSelectedMarket(onlyMarket);
      onFilterChange?.(selectedInstance, onlyMarket, searchTerm);
    }
  }, [markets]);

  const loadProducts = async () => {
    if (!selectedInstance || !selectedMarket) return;
    
    setIsLoading(true);
    try {
      const filterOptions: MarketItemFilterOptions = {
        companyId: selectedInstance,
        customerTypeId: selectedMarket,
        searchTerm: searchTerm || undefined,
        sort: sortColumn,
        direction: sortDirection,
        pageNumber: currentPage,
        pageSize: pageSize,
      };

      const response = await client.getMarketItems(filterOptions);
      
      if (response.success && response.data) {
        setProducts(response.data.items || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalCount || 0);
      } else {
        setErrorMessage(response.message || t('loadErrorMessage'));
        setIsSubmittedSuccessfully(false);
        setShowSubmitStatusMessage(true);
      }
    } catch (error) {
      logError('Failed to load products');
      setErrorMessage(t('loadErrorMessage'));
      setIsSubmittedSuccessfully(false);
      setShowSubmitStatusMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSortChange = (column: string) => {
    const isAsc = sortColumn === column && sortDirection === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';
    setSortDirection(newDirection);
    setSortColumn(column);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, selectedMarket, searchTerm, column, newDirection, 0);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    onFilterChange?.(selectedInstance, selectedMarket, searchTerm, sortColumn, sortDirection, page);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, selectedMarket, searchInput, sortColumn, sortDirection, 0);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    const newInstance = event.target.value;
    setSelectedInstance(newInstance);
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.(newInstance, '', searchTerm, sortColumn, sortDirection, 0);
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    const newMarket = event.target.value;
    setSelectedMarket(newMarket);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, newMarket, searchTerm, sortColumn, sortDirection, 0);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedInstance('');
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.('', '', '', sortColumn, sortDirection, 0);
  };

  const onClear = () => {
    handleClear();
  };

  const handleCloseMessage = () => {
    setShowSubmitStatusMessage(false);
  };

  const maxPages = () => {
    return Math.max(1, totalPages);
  };

  const showSelectMessage = !selectedInstance || !selectedMarket;

  return (
    <>
      <Grid container
        spacing={5}>
        <Grid item
          lg={3}
          sm={6}
          xs={12}>
          <TextField
            label={t('search')}
            placeholder={t('searchPlaceholder')}
            value={searchInput}
            onChange={(event) => handleSearchInputChange(event.target.value)}
            onKeyPress={handleSearchKeyPress}
            fullWidth
            disabled={showSelectMessage}
          />
        </Grid>
        <Grid item
          lg={2}
          sm={3}
          xs={6}>
          <Button
            fullWidth
            onClick={handleClear}
            variant='secondary'
            size='lg'
            disabled={showSelectMessage}
          >
            {t('clear')}
          </Button>
        </Grid>
        <Grid item
          lg={2}
          sm={3}
          xs={6}>
          <Button
            fullWidth
            onClick={handleSearch}
            size='lg'
            startIcon={<SvgIcon><SearchLg /></SvgIcon>}
            disabled={showSelectMessage}
          >
            {t('search')}
          </Button>
        </Grid>
        <Grid item
          lg={2.5}
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink>
              {t('filterByInstance')}
            </InputLabel>
            <Select
              value={instances.some(m => m.id?.toString() == selectedInstance) ? selectedInstance : ''}
              onChange={handleInstanceChange}
              label={t('filterByInstance')}
              displayEmpty
              notched
            >
              <MenuItem value="">
                <em>{t('selectInstance')}</em>
              </MenuItem>
              {instances.map((instance) => (
                <MenuItem key={instance.id}
                  value={instance.id.toString()}>
                  {instance.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          lg={2.5}
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink>
              {t('filterByMarket')}
            </InputLabel>
            <Select
              value={markets.some(m => m.customerTypeId?.toString() == selectedMarket) ? selectedMarket : ''}
              onChange={handleMarketChange}
              label={t('filterByMarket')}
              displayEmpty
              notched
              disabled={!selectedInstance}
            >
              <MenuItem value="">
                <em>{t('selectMarket')}</em>
              </MenuItem>
              {markets.map((market) => (
                <MenuItem key={market.customerTypeId}
                  value={market.customerTypeId.toString()}>
                  {market.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          xs={12}>
          <Section>
            {showSelectMessage ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '700px',
                  textAlign: 'center',
                }}
              >
                <Typography variant="textMD"
                  color="text.secondary"
                  gutterBottom>
                  {t('selectInstanceAndMarket')}
                </Typography>
              </Box>
            ) : (
              <>
                <ProductsList
                  list={products}
                  isLoading={isLoading}
                  totalCount={totalItems}
                  onClear={onClear}
                  onCreateNew={onCreateNew}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSortChange={handleSortChange}
                />
                {!isLoading && (
                  <Pagination
                    page={currentPage}
                    setPage={handlePageChange}
                    maxPages={maxPages()}
                    breakpointForChangeDisplay={120000}
                    useNoDataMessage={false}
                  />
                )}
              </>
            )}
          </Section>
        </Grid>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showSubmitStatusMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSubmittedSuccessfully ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {isSubmittedSuccessfully ? (errorMessage || t('successMessage')) : errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductsTab;
